import { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import { handleFailedResponse } from '@agilelab/plugin-wb-platform-common';
import {
  BusinessConceptDetails,
  BusinessConceptDomainDetails,
  BusinessConceptDomainsResponse,
  BusinessConceptFactPage,
  BusinessConceptHierarchy,
  BusinessConceptMatchByLabelRequest,
  BusinessConceptMatchByLabelResponse,
  BusinessConceptMembership,
  BusinessConceptRelationFact,
  GetBusinessConceptDomainDetailsRequest,
  ListBusinessConceptDomainsRequest,
  BusinessConceptRelationsResponse,
  BusinessConceptSearchRequest,
  BusinessConceptSearchResponse,
  BusinessConceptSummariesRequest,
  BusinessConceptSummariesResponse,
  BusinessConceptSummary,
  BusinessConceptsApi,
  BusinessConceptsRequestOptions,
  GetBusinessConceptDetailsRequest,
  GetBusinessConceptRequest,
  ListBusinessConceptRelationsRequest,
  ListBusinessConceptFactsRequest,
  ListBusinessConceptTypedFactsRequest,
} from '@agilelab/plugin-wb-marketplace-common';

export type ConceptMembership = BusinessConceptMembership;

export type ConceptHierarchy = BusinessConceptHierarchy;

export type ConceptRelationFact = BusinessConceptRelationFact;

/**
 * Client implementation for the vocabulary-neutral Business Concepts API
 * @public
 */
export class BusinessConceptsClient implements BusinessConceptsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('marketplace');
  }

  private async get<T>(
    path: string,
    params: URLSearchParams,
    signal?: AbortSignal,
  ): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const query = params.toString();
    const url = `${baseUrl}${path}${query ? `?${query}` : ''}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      ...(signal ? { signal } : {}),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}${path}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async listBusinessConceptDomains(
    request: ListBusinessConceptDomainsRequest,
    options?: BusinessConceptsRequestOptions,
  ): Promise<BusinessConceptDomainsResponse> {
    const params = new URLSearchParams();
    if (request.offset !== undefined)
      params.set('offset', String(request.offset));
    if (request.limit !== undefined) params.set('limit', String(request.limit));
    return this.get('/v1/business-concepts/domains', params, options?.signal);
  }

  async listBusinessConceptRoots(
    request: ListBusinessConceptFactsRequest,
    options?: BusinessConceptsRequestOptions,
  ): Promise<BusinessConceptFactPage<string>> {
    const params = new URLSearchParams();
    if (request.offset !== undefined)
      params.set('offset', String(request.offset));
    if (request.limit !== undefined) params.set('limit', String(request.limit));
    return this.get('/v1/business-concepts/roots', params, options?.signal);
  }

  async listRootConceptMemberships(
    request: ListBusinessConceptTypedFactsRequest,
    options?: BusinessConceptsRequestOptions,
  ): Promise<BusinessConceptFactPage<ConceptMembership>> {
    const params = new URLSearchParams();
    if (request.offset !== undefined)
      params.set('offset', String(request.offset));
    if (request.limit !== undefined) params.set('limit', String(request.limit));
    if (request.includeType !== undefined)
      params.set('includeType', String(request.includeType));
    return this.get(
      '/v1/business-concepts/root-memberships',
      params,
      options?.signal,
    );
  }

  async listBusinessConceptHierarchies(
    request: ListBusinessConceptFactsRequest,
    options?: BusinessConceptsRequestOptions,
  ): Promise<BusinessConceptFactPage<ConceptHierarchy>> {
    const params = new URLSearchParams();
    if (request.offset !== undefined)
      params.set('offset', String(request.offset));
    if (request.limit !== undefined) params.set('limit', String(request.limit));
    return this.get(
      '/v1/business-concepts/hierarchies',
      params,
      options?.signal,
    );
  }

  async listBusinessConceptNamedRelations(
    request: ListBusinessConceptFactsRequest,
  ): Promise<BusinessConceptFactPage<ConceptRelationFact>> {
    const params = new URLSearchParams();
    if (request.offset !== undefined)
      params.set('offset', String(request.offset));
    if (request.limit !== undefined) params.set('limit', String(request.limit));
    return this.get('/v1/business-concepts/concept-relations', params);
  }

  async getBusinessConceptDomainDetails(
    request: GetBusinessConceptDomainDetailsRequest,
  ): Promise<BusinessConceptDomainDetails> {
    return this.get(
      '/v1/business-concepts/domains/details',
      new URLSearchParams({ iri: request.iri }),
    );
  }

  async getBusinessConcept(
    request: GetBusinessConceptRequest,
  ): Promise<BusinessConceptSummary> {
    const params = new URLSearchParams({ iri: request.iri });
    return this.get('/v1/business-concepts', params);
  }

  async getBusinessConceptSummaries(
    request: BusinessConceptSummariesRequest,
  ): Promise<BusinessConceptSummariesResponse> {
    return this.post('/v1/business-concepts/summaries', {
      iris: request.iris,
    });
  }

  async matchBusinessConceptsByLabel(
    request: BusinessConceptMatchByLabelRequest,
  ): Promise<BusinessConceptMatchByLabelResponse> {
    return this.post('/v1/business-concepts/match-by-label', request);
  }

  async getBusinessConceptDetails(
    request: GetBusinessConceptDetailsRequest,
  ): Promise<BusinessConceptDetails> {
    const params = new URLSearchParams({ iri: request.iri });
    return this.get('/v1/business-concepts/details', params);
  }

  async listBusinessConceptRelations(
    request: ListBusinessConceptRelationsRequest,
  ): Promise<BusinessConceptRelationsResponse> {
    const params = new URLSearchParams({ iri: request.iri });
    return this.get('/v1/business-concepts/relations', params);
  }

  async searchBusinessConcepts(
    request: BusinessConceptSearchRequest,
  ): Promise<BusinessConceptSearchResponse> {
    return this.post('/v1/business-concepts/search', request);
  }
}
